import React, { useRef, useMemo, useEffect } from "react";
import { connect } from "react-redux";

import { compose, withState, withHandlers } from "recompose";
import {
  MAIN_COLOR,
  MenuType,
  PermissionType,
  MAPBOX_ACCESSTOKEN,
  LAYER_IDS,
} from "../../shared/utils/constant";
import { Button, ButtonGroup, Tooltip } from "@material-ui/core";
import { Trans } from "react-i18next";
import { CloudDownload as DownloadIcon } from "@material-ui/icons";
import { setSurveyedMapType, getVectorSourceInfo } from "../../services/actions/GisAction";
import moment from "moment";
import shpwrite from "@mapbox/shp-write";
import { saveAs } from "file-saver";
import {
  getColorByDistress,
  HasPermissions,
} from "../../shared/utils/objectExtensions";
import { ShowPermissionRequired } from "../../services/actions/SystemAction";
import { loading, resetLoading } from "../../services/actions/RoadAction";

import {
  Map as MapboxMap,
  Layer,
  Source,
  NavigationControl,
  FullscreenControl as MapboxFullscreenControl
} from 'react-map-gl';

import { bbox as tBBox, center as tCenter } from "@turf/turf";
import { authFileFetch } from "../../shared/utils/fetchUtils";
import { ApiEndpoint } from "../../env.config";

const Map = ({
  data,
  surveyedMapType,
  setMapType,
  downloadShapeFile,
  surfaces: rulesSurfaceCondition,
  shpData,
  selectedRow
}) => {
  const mapEl = useRef(null);

  const mapZoom = 16;

  const convertHexToRGBA = (hex) => {
    // 空チェック
    if (hex == null || hex == '') {
      return hex;
    }

    // 9文字の長さかどうかを確認
    if (hex.length !== 9) {
      return hex;
    }
    
    // 先頭の '#' を取り除く
    if (hex.startsWith('#')) {
      hex = hex.slice(1);
    } else {
      return hex;
    }

    // 色部分と透明度部分に分割
    const rHex = hex.slice(0, 2);
    const gHex = hex.slice(2, 4);
    const bHex = hex.slice(4, 6);
    const aHex = hex.slice(6, 8);

    // 16進数から10進数に変換
    const r = parseInt(rHex, 16);
    const g = parseInt(gHex, 16);
    const b = parseInt(bHex, 16);
    const a = parseInt(aHex, 16);

    // 透明度を0から1の範囲に変換
    const alpha = (a / 255).toFixed(2);

    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  };

  // スタイル(色)の設定
  const yearPaintColorStyle = [];
  rulesSurfaceCondition.forEach((s, index) => {
    if (index == 0) yearPaintColorStyle.push('case');
    if (s.Value) {
      const colorExpressions = ['==', ['get', 'distressAverage'], s.value]
      yearPaintColorStyle.push(colorExpressions)
      const color = convertHexToRGBA(s.color);
      yearPaintColorStyle.push(color)
    } else {
      const colorExpressions = ['boolean', true, true]
      yearPaintColorStyle.push(colorExpressions)
      yearPaintColorStyle.push(MAIN_COLOR)
    }
    if (index == rulesSurfaceCondition.length - 1) yearPaintColorStyle.push(MAIN_COLOR)
  });

  // LINEのベクタータイル用のスタイル(step2ではpaint部のみ使用)
  const lineLayerStyle = {
    "id": LAYER_IDS.LINE,
    "type": 'line',
    "source-layer": getVectorSourceInfo(data, "line").name,
    "paint": {
      'line-width': [
        'case',
        ['boolean', ['feature-state', 'hover'], false], // hoverの値取得、デフォルト値false、の意
        6,  // hover時
        3,  // 通常時
      ],
      'line-color': rulesSurfaceCondition.length !== 0 ? yearPaintColorStyle : MAIN_COLOR,
    }
  };

  // POINTのベクタータイル用のスタイル(step2ではpaint部のみ使用)
  const pointLayerStyle = {
    "id": LAYER_IDS.POINT,
    "type": 'circle',
    "source-layer": getVectorSourceInfo(data, "point").name,
    "paint": {
      'circle-radius': [
        'case',
        ['boolean', ['feature-state', 'hover'], false], // hoverの値取得、デフォルト値false、の意
        8,  // hover時
        5   // 通常時
      ],
      'circle-color': rulesSurfaceCondition.length !== 0 ? yearPaintColorStyle : MAIN_COLOR,
    }
  };


  // GeoJSONレイヤ処理
  // Polylineのデータ読み込み
  const lineData = useMemo(() => {
    if (data != null) {
      const featureDatas = [];
      data.map((x, idx) => {
        const featureData = {
          'id': String(idx),
          'type': 'Feature',
          'properties': {
            'distressAverage': x.distressAverage,
            'distressAverageColor': getColorByDistress(x.distressAverage, rulesSurfaceCondition),
            'surveyedDate': x.surveyedDate,
            // 'picPaths': x.picPaths,
            // 'polylines': x.polylines,
            // 'polylineDistress': x.polylineDistress,
            'routeName': x.routeName
          },
          'geometry': {
            'type': 'LineString',
            'coordinates': x.snappedPoints,
          },
        }
        featureDatas.push(featureData)
      })
      return {
        'type': 'FeatureCollection',
        'features': featureDatas
      }
    } else {
      return null;
    }
  }, [data]);
  // (Point)のデータ読み込み
  const pointData = useMemo(() => {
    if (data != null) {
      const featureDatas = [];
      data.map((x, idx) => {
        x.polylines.map((p, pidx) => {
          const pointDistress = x.polylineDistress.find(
            (d) => d.polylines === JSON.stringify(p)
          );
          const valueDistress = pointDistress ? pointDistress.distressAverage : x.distressAverage;
          const color = getColorByDistress(valueDistress, rulesSurfaceCondition);
          // console.log(`len: ${x.picPaths.length}`);

          const featureData = {
            'id': String(idx) + String(pidx),
            'type': 'Feature',
            'properties': {
              'id': String(idx) + String(pidx),
              'distressAverage': x.distressAverage,
              'distressAverageColor': color,
              'surveyedDate': x.surveyedDate,
              // 'picPaths': picPathsTmp,//x.picPaths,
              // 'polylines': x.polylines,
              // 'polylineDistress': x.polylineDistress,
              'routeName': x.routeName,
              // 'point': p
            },
            'geometry': {
              'type': 'Point',
              'coordinates': p,
            },
          }
          featureDatas.push(featureData)
        }
        )
      })
      return {
        'type': 'FeatureCollection',
        'features': featureDatas
      }
    } else {
      return null;
    }
  }, [data]);

  let hoverList = [];

  // ホバー処理(POINT/LINE共通)
  const handleMouseMove = (e) => {
    mapEl.current.getCanvas().style.cursor = 'default';
    if (e.features.length === 0) return;
    const featureID = e.features[0].id;
    if (featureID == null) return;
    // 既存ホバー地物をクリア
    for (const d of hoverList) {
      mapEl.current.removeFeatureState(d);
    }
    hoverList = [];
    // 今回のヒット地物をホバー状態に
    const info = {
      source: e.features[0].source,
      sourceLayer: e.features[0].sourceLayer,
      id: featureID
    };

    mapEl.current.setFeatureState(info, {
      hover: true
    });
    hoverList.push(info);
  }

  // リーブ処理(POINT/LINE共通)
  const handleMouseLeave = (e) => {
    mapEl.current.getCanvas().style.cursor = '';
    for (const d of hoverList) {
      mapEl.current.removeFeatureState(d);
    }
    hoverList = [];
  }

  // 監視処理
  useEffect(() => {
    if (!mapEl.current) return;
    // 中心BBox取得
    let currentCenterCoords = [0, 0];
    let currentBBox = [0, 0, 0, 0];
    if (surveyedMapType === "point" && pointData.features.length > 0) {
      currentBBox = tBBox(pointData);
      currentCenterCoords = tCenter(pointData).geometry.coordinates;
    } else if (surveyedMapType === "line" && lineData.features.length > 0) {
      currentBBox = tBBox(lineData);
      currentCenterCoords = tCenter(lineData).geometry.coordinates;
    }
    mapEl.current.fitBounds(currentBBox, {
      duration: 0   // アニメーションの期間を0に設定
    });
  }, [pointData, lineData]);

  // 地図ロード時処理
  const handleLoad = (evt) => {
    if (!mapEl.current) return;

    mapEl.current.on('mousemove', LAYER_IDS.POINT, handleMouseMove);
    mapEl.current.on('mouseleave', LAYER_IDS.POINT, handleMouseLeave);
    mapEl.current.on('mousemove', LAYER_IDS.LINE, handleMouseMove);
    mapEl.current.on('mouseleave', LAYER_IDS.LINE, handleMouseLeave);
  }


  return (
    <MapboxMap
      ref={mapEl}
      mapboxAccessToken={MAPBOX_ACCESSTOKEN}
      maxZoom={17}
      minZoom={1}
      dragRotate={false}
      touchZoomRotate={false}
      logoPosition="bottom-right"
      initialViewState={{
        zoom: mapZoom
      }}
      style={{ height: `calc(100% - 64px)` }}
      mapStyle="mapbox://styles/mapbox/light-v11"
      onLoad={handleLoad}>
      <NavigationControl showCompass={false} />
      <MapboxFullscreenControl />
      <ButtonGroup size="small" className="map-panel-type" style={{ position: 'absolute', top: '120px', right: '10px' }}>
        <Button
          className={`${surveyedMapType === "point" ? "type-selected" : ""}`}
          onClick={() => setMapType("point")}>
          Point
        </Button>
        <Button
          className={`${surveyedMapType === "line" ? "type-selected" : ""}`}
          onClick={() => setMapType("line")}>
          Line
        </Button>
      </ButtonGroup>
      <ButtonGroup size="small" style={{ position: 'absolute', top: '160px', right: '10px' }}>
        <Button
          variant="outlined"
          size="small"
          className="btn-download-shape-file"
          onClick={() => downloadShapeFile(shpData, selectedRow)}>
          <Tooltip title={<Trans>download_shapefile</Trans>} aria-label="add">
            <DownloadIcon />
          </Tooltip>
        </Button>
      </ButtonGroup>
      {data != null && lineData != null && surveyedMapType === "line" && (
        <Source id={LAYER_IDS.LINE} type="geojson" data={lineData}>
          <Layer {...lineLayerStyle} />
        </Source>
      )}
      {data != null && pointData != null && surveyedMapType === "point" && (
        <Source id={LAYER_IDS.POINT} type="geojson" data={pointData}>
          <Layer {...pointLayerStyle} />
        </Source>
      )}
    </MapboxMap>
  );
};

export default compose(
  withState("zoom", "setZoom", 16),
  withState("center", "setCenter", [39.94792279, -79.99223758]),
  connect(
    (state) => ({
      surveyedAllocated: state.road.surveyedAllocated,
      showAttributeList: state.road.showAttributeList,
      surveyedMapType: state.gis.surveyedMapType,
      surfaces: state.surface.surfaces,
      userFilter: state.road.userFilter,
      loggedUser: state.auth.user,
    }),
    (dispatch) => ({
      setMapType: (value) => dispatch(setSurveyedMapType(value)),
      showPermissionRequired: () => dispatch(ShowPermissionRequired()),
      showLoading: () => dispatch(loading()),
      hideLoading: () => dispatch(resetLoading()),
    })
  ),
  withHandlers({
    downloadShapeFile:
      ({
        surveyedMapType,
        showPermissionRequired,
        surfaces,
        userFilter,
        loggedUser,
        showLoading,
        hideLoading,
      }) => async (shpData, selectedRow) => {
        showLoading();
        try {
          if (!HasPermissions(MenuType.SurveyedList, PermissionType.Download)) {
            hideLoading();
            return showPermissionRequired();
          }
          const fileName = `pms_shapefile_${surveyedMapType === "line" ? "line_" : "point_"}${moment().format("DDMMYYYYhhmmss")}`;
          var options = {
            file: fileName,
            folder: fileName,
            outputType: "blob",
            types: {
              point: "points",
              polygon: "polygons",
              polyline: "polylines",
            },
          };

          let features = [];
          for (const fileInfo of selectedRow) { // 表示しているファイル数分実施する
            const fileId = fileInfo.id;
            const userId = fileInfo.uploadedFor;

            const surveyedAllocatedTest = await (await authFileFetch(
              `${ApiEndpoint}/Road/surveyed/${userId}/file/${fileId}/type/${surveyedMapType}`,
              { method: "GET" }
            )).json();
            features = features.concat(surveyedAllocatedTest);
          }
          shpwrite.zip({
            type: "FeatureCollection",
            features: features,
          }, options)
            .then((blob) => {
              saveAs(blob, options.file + '.zip');
              hideLoading();
            });
        } catch (error) {
          console.error(error);
          hideLoading();
        }
      },
  })
)(Map);
